import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import useTypedFlags from "hooks/useTypedFlags";

import { GET_INTEGRATIONS_COUNT, IntegrationsCountData } from "../gql";
import { getIntegrationById, integrationIds } from "../registry";
import { IntegrationId } from "../registry/constants";

const INTEGRATION_ID_TO_COUNT_KEY: Record<
  IntegrationId,
  keyof IntegrationsCountData["integrationsCount"] | undefined
> = {
  [IntegrationId.AWS]: "aws",
  [IntegrationId.AZURE]: "azure",
  [IntegrationId.AZURE_DEVOPS]: "azureDevOps",
  [IntegrationId.BACKSTAGE]: "backstage",
  [IntegrationId.BITBUCKET_CLOUD]: "bitbucketCloud",
  [IntegrationId.BITBUCKET_DATA_CENTER]: "bitbucketDatacenter",
  [IntegrationId.GITHUB]: "github",
  [IntegrationId.GITLAB]: "gitlab",
  [IntegrationId.SERVICENOW]: "serviceNow",
  [IntegrationId.SLACK]: "slack",
  [IntegrationId.VCS_AGENT_POOLS]: "vcsAgentPools",
  [IntegrationId.WEBHOOKS]: "webhooks",
  [IntegrationId.AI]: "ai",
  [IntegrationId.DATADOG]: undefined,
  [IntegrationId.GCP]: undefined,
  [IntegrationId.GRAPHQL_API]: undefined,
  [IntegrationId.SPACECTL]: undefined,
  [IntegrationId.TEAMS]: undefined,
  [IntegrationId.TERRAFORM_PROVIDER]: undefined,
};

export const useIntegrations = () => {
  const { useIntegrationsCounts, intentNewLayout } = useTypedFlags();
  const { data, loading, error } = useQuery(GET_INTEGRATIONS_COUNT, {
    skip: !useIntegrationsCounts,
  });

  const integrations = useMemo(() => {
    const integrations = integrationIds
      .map((id) => getIntegrationById(id, { intentNewLayout }))
      .filter((integration) => integration !== undefined);

    if (!data) {
      return integrations;
    }

    // populate the count for the registry integrations
    return integrations.map((integration) => {
      const countKey = INTEGRATION_ID_TO_COUNT_KEY[integration.id];
      if (!countKey) return integration;

      return {
        ...integration,
        count: data.integrationsCount[countKey],
      };
    });
  }, [data, intentNewLayout]);

  return {
    integrations,
    loading,
    error,
  };
};
